import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaTools, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import Seo from '../../../shared/seo/Seo';
import PageHeader from 'features/common/PageHeader';
import createServiceData from 'features/services/data/serviceData';
import { ServiceData } from 'features/services/types';
import { sendQuoteRequestEmail, QuoteFormData } from '../../../services/quoteService';
import './QuotePage.css';
import config from "../../../config/config";
import { getCanonicalUrl, pageMeta } from 'shared/seo/seoMeta';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  serviceType: string;
  product?: string;
  message: string;
  urgency: 'urgent' | 'normal' | 'planned';
}

const QuotePage: React.FC = () => {
  const { t } = useTranslation();
  const serviceData: ServiceData = createServiceData(t);
  // const fileInputRef = useRef<HTMLInputElement>(null);
  // const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    product: '',
    message: '',
    urgency: 'normal'
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files && e.target.files.length > 0) {
  //     setFile(e.target.files[0]);
  //   }
  // };
  
  // const triggerFileInput = () => {
  //   if (fileInputRef.current) {
  //     fileInputRef.current.click();
  //   }
  // };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('quote.errors.nameRequired', 'Veuillez entrer votre nom');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('quote.errors.emailRequired', 'Veuillez entrer votre email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('quote.errors.emailInvalid', 'Veuillez entrer un email valide');
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t('quote.errors.phoneRequired', 'Veuillez entrer votre numéro de téléphone');
    }
    
    if (!formData.serviceType) {
      newErrors.serviceType = t('quote.errors.serviceRequired', 'Veuillez sélectionner un service');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const quoteData: QuoteFormData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.serviceType,
        subservice: '',
        message: formData.message,
        company: formData.company,
        urgency: formData.urgency
      };
      
      const success = await sendQuoteRequestEmail(quoteData);
      
      if (success) {
        // Success
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          serviceType: '',
          product: '',
          message: '',
          urgency: 'normal'
        });
        // setFile(null);
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setSubmitError(t('quote.errors.submitFailed', 'Une erreur est survenue. Veuillez réessayer plus tard.'));
      }
    } catch (error) {
      setSubmitError(t('quote.errors.submitFailed', 'Une erreur est survenue. Veuillez réessayer plus tard.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <Seo
        title={pageMeta.quote.title}
        description={pageMeta.quote.description}
        canonical={getCanonicalUrl(pageMeta.quote.canonicalPath)}
      />
      <PageHeader 
        title={t('quote.title', 'Demande de Devis')}
        subtitle={t('quote.subtitle', 'Obtenez un devis personnalisé pour vos besoins')}
        background="/images/background/quote-bg.webp"
      />
      
      <section className="quote-section">
        <div className="container">
          <motion.div 
            className="quote-container"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="quote-info">
              <h2>{t('quote.formTitle', 'Demandez votre devis')}</h2>
              <p>{t('quote.formDescription', 'Remplissez ce formulaire pour obtenir un devis personnalisé. Notre équipe vous contactera dans les plus brefs délais.')}</p>
              
              <div className="quote-benefits">
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <FaCheck />
                  </div>
                  <div className="benefit-text">
                    <h4>{t('quote.benefits.fast', 'Réponse Rapide')}</h4>
                    <p>{t('quote.benefits.fastDesc', 'Nous vous répondons sous 24h')}</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <FaCheck />
                  </div>
                  <div className="benefit-text">
                    <h4>{t('quote.benefits.custom', 'Devis Personnalisé')}</h4>
                    <p>{t('quote.benefits.customDesc', 'Adapté à vos besoins spécifiques')}</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <FaCheck />
                  </div>
                  <div className="benefit-text">
                    <h4>{t('quote.benefits.quality', 'Service de Qualité')}</h4>
                    <p>{t('quote.benefits.qualityDesc', 'Expertise et professionnalisme')}</p>
                  </div>
                </div>
              </div>
              
              <div className="contact-info-quote">
                <h4>{t('quote.contactInfo', 'Nous contacter directement')}</h4>
                <p style={{direction: 'ltr'}}>
                  <FaPhone />   {config.contact.phone}<br />
                  <FaEnvelope />   {config.contact.email}
                </p>
              </div>
            </div>
            
            <div className="quote-form-container">
              
              {submitError && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  <p>{submitError}</p>
                </div>
              )}
              
              <form className="quote-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      <FaUser /> {t('quote.form.name', 'Nom et Prénom')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={errors.name ? 'error' : ''}
                    />
                    {errors.name && <div className="error-text">{errors.name}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">
                      <FaEnvelope /> {t('quote.form.email', 'Email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <div className="error-text">{errors.email}</div>}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">
                      <FaPhone /> {t('quote.form.phone', 'Téléphone')} *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && <div className="error-text">{errors.phone}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company">{t('quote.form.company', 'Entreprise (facultatif)')}</label>
                    <div className={`input-wrapper ${errors.company ? 'has-error' : ''}`}>
                      <span className="input-icon"><FaBuilding /></span>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder={t('quote.form.companyPlaceholder', 'Nom de votre entreprise')}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="serviceType">
                    <FaTools /> {t('quote.form.service', 'Type de Service')} *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    className={errors.serviceType ? 'error' : ''}
                  >
                    <option value="">{t('quote.form.selectService', 'Sélectionnez un service')}</option>
                    {Object.values(serviceData).map(service => {
                      const title = typeof service.title === 'object' && 'fr' in service.title ? (service.title as { fr: string }).fr : String(service.title);
                      return (
                        <option key={service.id} value={title}>
                          {title}
                        </option>
                      );
                    })}
                  </select>
                  {errors.serviceType && <div className="error-text">{errors.serviceType}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="urgency">{t('quote.form.urgency', 'Niveau d\'urgence')}</label>
                  <div className="urgency-options">
                    <label className={`urgency-option ${formData.urgency === 'urgent' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="urgency"
                        value="urgent"
                        checked={formData.urgency === 'urgent'}
                        onChange={handleInputChange}
                      />
                      <span>{t('quote.form.urgent', 'Urgent')}</span>
                    </label>
                    <label className={`urgency-option ${formData.urgency === 'normal' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="urgency"
                        value="normal"
                        checked={formData.urgency === 'normal'}
                        onChange={handleInputChange}
                      />
                      <span>{t('quote.form.normal', 'Normal')}</span>
                    </label>
                    <label className={`urgency-option ${formData.urgency === 'planned' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="urgency"
                        value="planned"
                        checked={formData.urgency === 'planned'}
                        onChange={handleInputChange}
                      />
                      <span>{t('quote.form.planned', 'Planifié')}</span>
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">
                    {t('quote.form.message', 'Détails de votre demande')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t('quote.form.messagePlaceholder', 'Décrivez votre projet ou vos besoins en détail...')}
                  ></textarea>
                </div>
                
                {/*<div className="form-group">*/}
                {/*  <div className="file-upload" onClick={triggerFileInput}>*/}
                {/*    <input*/}
                {/*      type="file"*/}
                {/*      ref={fileInputRef}*/}
                {/*      onChange={handleFileChange}*/}
                {/*      style={{ display: 'none' }}*/}
                {/*    />*/}
                {/*    <div className="file-upload-button">*/}
                {/*      <FaUpload />*/}
                {/*      <span>{t('quote.form.attachFile', 'Joindre un fichier')}</span>*/}
                {/*    </div>*/}
                {/*    {file && (*/}
                {/*      <div className="file-info">*/}
                {/*        <FaFile />*/}
                {/*        <span>{file.name}</span>*/}
                {/*      </div>*/}
                {/*    )}*/}
                {/*  </div>*/}
                {/*</div>*/}


                {submitSuccess && (
                    <div className="success-message">
                      <FaCheck />
                      <p>{t('quote.successMessage', 'Votre demande a été envoyée avec succès. Nous vous contacterons bientôt.')}</p>
                    </div>
                )}

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting 
                    ? t('quote.form.sending', 'Envoi en cours...') 
                    : t('quote.form.submit', 'Envoyer ma demande')}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default QuotePage;
